import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  AfterViewInit
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpClientModule} from '@angular/common/http';
import {RouterModule} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Observable, Subject, takeUntil, filter, of, from, forkJoin} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';

// PrimeNG imports
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {SelectModule} from 'primeng/select';
import {MultiSelectModule} from 'primeng/multiselect';
import {DatePickerModule} from 'primeng/datepicker';
import {CheckboxModule} from 'primeng/checkbox';
import {TooltipModule} from 'primeng/tooltip';
import {MessageService} from 'primeng/api';
import {ToastModule} from 'primeng/toast';

// OpenStreetMap
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {MapControllerInput, MapInitOptions} from '@components/openstreetmap/interfaces';
import {
  MapDrawLeafletLayer,
  MapDrawShapeCreatedPayload,
} from '@components/openstreetmap/controllers/draw/map-drawing.types';
// Managers
import {GisMapControllerManager} from './managers/gis-map-controller.manager';

// Existing managers (kept for compatibility)
import {MonitoringPointsManager} from './managers/monitoring-points.manager';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';

// Components
import {
  PointPolygonDetailsModalComponent
} from '@app/pages/weather/pages/gis-weather/components/point-polygon-details-modal/point-polygon-details-modal.component';
import {
  MonitoringPointsDialogComponent
} from './components/monitoring-points-dialog/monitoring-points-dialog.component';
import {ExportDialogComponent} from './components/export-dialog/export-dialog.component';
import {ListPageWrapper} from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import {Filters} from '@components/views/filters/filters';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';

// Environment
import {environment} from '@root/src/environments/environment';
import {CropService, RegionService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {MapContextMenuComponent} from '@app/pages/weather/pages/gis-weather/components/map-context-menu/map-context-menu.component';
import {
  AllPointsChartModalComponent
} from '@app/pages/weather/pages/gis-weather/components/all-points-chart-modal/all-points-chart-modal.component';
import {PolygonValidationService} from '@app/pages/weather/pages/gis-weather/services/polygon-validation.service';
import {WeatherStationManager} from '@components/openstreetmap/managers/weather-statition.manager';
import {TilesManager} from '@app/pages/weather/pages/gis-weather/managers/tiles.manager';
import {BackButton} from '@components/ui/back-button/back-button';
import {FilterOperator} from '@app/utils/filter-operator';
import {DataTransformer} from '@app/utils/data-transformer';
import {pointInPolygon, polygonsAreEquivalent} from '@app/utils/polygons.utils';
import {MapMonitoringObject} from '@6grain/masofa-monolith-api-client/map';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {WeatherLegendComponent} from '@app/pages/weather/pages/gis-weather/components/weather-legend';
import {WeatherLegendRangeService} from '@app/pages/weather/pages/gis-weather/components/weather-legend/weather-legend-range.service';
import {LegendValueRange} from '@app/pages/weather/pages/gis-weather/components/weather-legend/legend-range.util';
import {
  yearlyAtdDropdownLabel,
  yearlyAtdDropdownTitleKey
} from '@app/pages/weather/pages/gis-weather/components/weather-legend/legend-match.util';
import {CropsManagerService} from '@app/pages/weather/pages/gis-weather/managers/crops-manager.service';
import {attachWeatherGridStepKeys} from '@app/pages/weather/pages/gis-weather/utils/weather-data-source-options.util';
import {
  isExcludedFromWeatherDataType,
  isYearlyWeatherLayerName,
  stripWeatherLayerPeriod
} from '@app/pages/weather/pages/gis-weather/utils/weather-layer-name.util';
import {mapWeatherLayerSelectOptions} from '@app/pages/weather/pages/gis-weather/utils/weather-layer-select.util';
import {ServerTranslateService} from '@services/server-translate.service';

type OverlayLayerKey = 'weather' | 'index' | 'satellite';

interface OverlayLayerState {
  visible: boolean;
  opacity: number;
}


@Component({
  selector: 'app-gis-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    CheckboxModule,
    TooltipModule,
    ToastModule,
    BackButton,
    OpenStreetMapComponent,
    PointPolygonDetailsModalComponent,
    MonitoringPointsDialogComponent,
    ListPageWrapper,
    Filters,
    MapContextMenuComponent,
    AllPointsChartModalComponent,
    ExportDialogComponent,
    WeatherLegendComponent,

  ],
  providers: [MessageService],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gis-page.component.refactored.new.html',
  styleUrls: ['./gis-page.component.refactored.new.scss']

})
export class GisPageRefactoredNewComponent implements OnInit, AfterViewInit, OnDestroy {
  // =========================================================================
  // DEPENDENCIES INJECTION
  // =========================================================================

  private translate = inject(TranslateService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private polygonValidationService = inject(PolygonValidationService);
  private messageService = inject(MessageService);

  private cropService = inject(CropService);
  private regionService = inject(RegionService);

  // Managers (New)
  private gisMapControllerManager = inject(GisMapControllerManager);

  // Managers (Existing - keep for compatibility during transition)
  private regionManagementManager = inject(RegionManagerService);
  public monitoringPointManager = inject(MonitoringPointsManager);
  public weatherStationManager = inject(WeatherStationManager);
  public tilesManager = inject(TilesManager);

  private destroy$ = new Subject<void>();
  private readonly regionByPointRequest$ = new Subject<{ lat: number; lng: number }>();

  showAllPointsChartModal: boolean = false;

  // =========================================================================
  // MAP CONTROLLER & OPTIONS
  // =========================================================================

  mapController$ = new Subject<MapControllerInput>();

  mapOptions: MapInitOptions = {
    height: '100%',
    indexOpacityToggle: false,
    center: [
      environment.map.center?.lat ?? environment.map.lat,
      environment.map.center?.lng ?? environment.map.lng
    ],
    zoom: 8,
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: true,
    baseMap: 'osm',
    showRegionNames: true,
    showUgmStations: false,
    disableBounds: false
  };

  // =========================================================================
  // UI STATE
  // =========================================================================

  selectedWeatherLayer: string | null = null;
  layerStates: Record<OverlayLayerKey, OverlayLayerState> = {
    weather: {visible: true, opacity: 100},
    index: {visible: true, opacity: 60},
    satellite: {visible: true, opacity: 100}
  };
  topLayer: OverlayLayerKey | null = null;
  showContextMenu: boolean = false;
  contextMenuPosition = {top: 0, left: 0};
  contextMenuData: any = null;
  showWeatherDetailsModal: boolean = false;
  /** Leaflet layer for the last drawn monitoring polygon/rectangle awaiting save in the weather modal. */
  private pendingUnsavedMonitoringDrawLayer: MapDrawLeafletLayer | null = null;
  showMonitoringPointsModal: boolean = false;
  showExportModal: boolean = false;
  exportCoordinates: { latitude: number, longitude: number } | null = null;
  showWeatherLegend: boolean = false;
  legendClipToRange = false;
  legendDisplayedRange: LegendValueRange | null = null;
  private readonly legendRangeService = inject(WeatherLegendRangeService);
  private readonly legendRangeCancel$ = new Subject<void>();
  /** Admin path for the open weather modal — from click coordinates, not hover. */
  selectedPointRegionPath = '';

  // =========================================================================
  // MAP STATE
  // =========================================================================

  mapCenter: [number, number] = [environment.map.lat, environment.map.lng];
  mapZoom: number = 6;
  currentDrawingState = {
    isDrawing: false,
    drawingType: null as 'point' | 'polygon' | 'field' | 'rectangle' | null
  };

  private weatherTileLayer: any = null; // Reference to the weather tile layer
  mapLayers = {
    plan: {
      name: 'Plan',
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '© OpenStreetMap contributors'
    },
    satellite: {
      name: 'Satellite',
      url: 'https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      subdomains: ['0', '1', '2', '3'],
      attribution: '© Google'
    },
    weather: {
      name: 'Weather',
      baseUrl: `${environment.api}/tiles/Map/GetTile`,
      attribution: '© Weather Data'
    }
  };


  // =========================================================================
  // WEATHER & DATA STATE
  // =========================================================================

  selectedPointForWeather: any = null;
  leafletMarkers: any[] = [];
  isLoadingMonitoringObjects = false;

  // =========================================================================
  // FILTER STATE
  // =========================================================================

  filterModel: any = {};
  filterFields: IMsfFormBuilder[] = [];
  isLoadingFilters: boolean = false;

  // Filter options
  dataTypeOptions: any[] = [];
  dataSourceOptions: any[] = [];
  weatherLayerOptions: any[] = [];

  // Selected filter values
  selectedDataType: string | null = null;
  selectedDataSource: string | null = null;
  selectedDate: Date | null = null;
  selectedCrops: string | null = null;

  // Previous filter values for change detection
  private previousDataSource: string | null = null;
  private previousDataType: string | null = null;
  private previousCrop: string | null = null;
  private previousDate: string | null = null;
  private previousYear: number | null = null;
  /** Only the first page load auto-selects Temperature Average. */
  private applyDefaultWeatherLayerOnLoad = true;

  // tiles Indexes


  // =========================================================================
  // UI STATE (continued)
  // =========================================================================

  chartData: any = {labels: [], datasets: []};
  @ViewChild(OpenStreetMapComponent) openstreetmapComponent!: OpenStreetMapComponent;
  today: Date = new Date();


  ngOnInit(): void {
    this.initializeComponent();
    this.resetMapState();
    this.subscribeToRegionByPointRequests();
  }

  /**
   * Resolve oblast/district via server when the cached polygon hit-test has no district.
   * switchMap cancels the previous GET when the user clicks another point.
   */
  private subscribeToRegionByPointRequests(): void {
    this.regionByPointRequest$
      .pipe(
        switchMap(({lat, lng}) =>
          this.regionService.dictionariesRegionGetRegionByPointGet(lng, lat).pipe(
            catchError(() => of(null)),
            switchMap((region) => {
              if (!region?.id) {
                return of('');
              }
              return from(
                this.regionManagementManager.getRegionPathStringById(String(region.id), {
                  minLevel: 2
                })
              );
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((path) => {
        if (path) {
          this.selectedPointRegionPath = path;
        }
      });
  }

  onLegendClose(): void {
    this.openstreetmapComponent?.osmIndexOverlay?.clearIndexSelection();
  }

  hasWeatherLayer(): boolean {
    return !!this.selectedWeatherLayer;
  }

  hasIndexLayer(): boolean {
    return !!this.openstreetmapComponent?.osmIndexOverlay?.hasActiveIndexLayer();
  }

  getWeatherLayerLabel(): string {
    return this.selectedWeatherLayer || '';
  }

  getIndexLayerLabel(): string {
    return this.openstreetmapComponent?.osmIndexOverlay?.getActiveIndexLayerLabel() || this.openstreetmapComponent?.selectedIndexLayer || '';
  }

  onIndexLayerApplied(): void {
    this.layerStates.index.visible = true;
    const pct = this.openstreetmapComponent?.indexManager?.getIndexTileOpacityPercent?.();
    if (typeof pct === 'number' && Number.isFinite(pct)) {
      this.layerStates.index = {...this.layerStates.index, opacity: Math.max(0, Math.min(100, pct))};
    }
    this.openstreetmapComponent?.osmIndexOverlay?.setIndexLayerVisibility(true);
    this.topLayer = 'index';
    this.syncOverlayLayerOrder();
  }

  private resetMapState(): void {
    // this.leafletPolygons = [];
    this.leafletMarkers = [];

    this.mapController$.next({
      clearPolygons: true,
      clearMarkers: true
    });

    this.monitoringPointManager.clearMonitoringLayers?.();
    this.weatherStationManager.clearWeatherStationMarkers?.();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.waitForMapInitialization();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Wait for map to be fully initialized before subscribing to events
   */
  private waitForMapInitialization(): void {
    if (this.openstreetmapComponent?.isMapInitialized && this.openstreetmapComponent?.mapClick$) {
      // Map is ready, proceed with initialization
      this.loadInitialData();
      this.subscribeToMapEvents();
    } else {
      // Map not ready yet, check again after a short delay
      setTimeout(() => {
        this.waitForMapInitialization();
      }, 100);
    }
  }


  private initializeComponent(): void {
    // Set initial map options
    this.mapOptions.center = [
      environment.map.center?.lat ?? environment.map.lat,
      environment.map.center?.lng ?? environment.map.lng
    ];
    this.mapOptions.zoom = 6;

    this.initializeFilterOptions();

    this.initializeFilterFields();
    this.processFilterTypeAndSource();

    if (this.filterModel.DATA_TYPE) {
      this.tilesManager.initialize(this.openstreetmapComponent, this.filterModel.DATA_TYPE, this.filterModel.DATA_SOURCE);
    }

  }

  /**
   * Initialize filter fields for msf-filters component
   */
  private initializeFilterFields(): void {
    this.filterModel = {
      DATA_SOURCE: "era5",
      DATE: new Date(),
      YEAR: new Date().getFullYear(),
      DATA_TYPE: "weather"
    }
    this.filterFields = [
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagementManager,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',
          }
        },
        placeholder: 'Select region'
      },
      {
        name: 'DATA_SOURCE',
        title: 'DATA_SOURCE',
        type: 'select',
        selectOptions: this.dataSourceOptions,
        placeholder: 'SELECT_DATA_SOURCE'
      },
      {
        name: 'DATA_TYPE',
        title: 'DATA_TYPE',
        type: 'select',
        selectOptions: this.dataTypeOptions,
        placeholder: 'SELECT_DATA_TYPE',
      },
      {
        name: 'WEATHER_LAYER',
        title: 'WEATHER_LAYER',
        type: 'select',
        getSelectOptions: () => this.weatherLayerSelectOptions(),
        placeholder: 'SELECT_WEATHER_LAYER',
        options: {
          select: {
            optionValue: 'geoServerName',
            optionLabel: 'label',
            translateBy: 'none',
          }
        }
      },
      {
        name: 'YEAR',
        title: 'YEAR',
        type: 'date',
        placeholder: 'SELECT_YEAR',
        options: {
          date: {
            yearOnly: true,
          }
        }
      },
      {
        name: 'DATE',
        title: 'DATE',
        type: 'date',
        placeholder: 'Select date',
        options: {
          date: {
            onYearChange: (year?: number) => {
              this.tilesManager.GetWeatherLayerDates(
                year as number,
                this.filterModel.DATA_TYPE,
                this.filterModel.DATA_SOURCE
              )
                .pipe(takeUntil(this.destroy$))
                .subscribe()
            },
            selectableDates: () => {
              const dataType = this.filterModel.DATA_TYPE || 'weather';
              const dataSource = this.filterModel.DATA_SOURCE || 'era5';
              return this.tilesManager.getAllAvailableDatesByIndex(dataType, dataSource);
            },
            allowOnlySelectedDate: true,
          }
        }
      },
      {
        name: 'CROPS',
        title: 'CROPS',
        type: 'select-paginate',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
        placeholder: 'SELECT_CROP'
      }
    ];
  }

  /**
   * Initialize filter options
   */
  private initializeFilterOptions(): void {
    this.dataTypeOptions =
      [
        {
          "id": "normal",
          "names": {
            "en-US": "Normal",
            "ru-RU": "Норма",
            "uz-Latn-UZ": "Norma"
          }
        },
        {
          "id": "deviation",
          "names": {
            "en-US": "Deviation",
            "ru-RU": "Отклонение",
            "uz-Latn-UZ": "Og'ish"
          }
        },
        {
          "id": "weather",
          "names": {
            "en-US": "Weather",
            "ru-RU": "Погода",
            "uz-Latn-UZ": "Ob-havo"
          }
        }
      ];

    this.dataSourceOptions = attachWeatherGridStepKeys([
      {
        names: {
          "en-US": 'ERA5',
          "ru-RU": 'ERA5',
          "uz-Latn-UZ": 'ERA5'

        }, id: 'era5'
      },
      {
        names: {
          "en-US": 'IBM',
          "ru-RU": 'IBM',
          "uz-Latn-UZ": 'IBM'
        }, id: 'ibm',
      },
      {
        names: {
          "en-US": 'UGM',
          "ru-RU": 'УГМ',
          "uz-Latn-UZ": 'UGM'
        }, id: 'ugm'
      }
    ]);
  }

  private hasLoadedGeoFileLayers(): boolean {
    return this.openstreetmapComponent?.geoFileLoaderManager?.hasLoadedLayers?.() ?? false;
  }

  private isGeoFileFeatureClick(lat: number, lng: number): boolean {
    if (!this.hasLoadedGeoFileLayers()) {
      return false;
    }
    return !!this.openstreetmapComponent?.geoFileLoaderManager?.pickFeatureAt?.({lat, lng});
  }

  private subscribeToMapEvents(): void {
    // Subscribe to map right-click events
    if (this.openstreetmapComponent?.mapRightClick$) {
      this.openstreetmapComponent.mapRightClick$
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: any) => {
          this.handleMapRightClick(event);
        });
    }

    // Subscribe to map click events - open weather modal on empty space
    if (this.openstreetmapComponent?.mapClickSubject$) {
      this.openstreetmapComponent.mapClickSubject$
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: any) => {
          const drawingController = this.openstreetmapComponent.getDrawingController();

          const lat = event.lat;
          const lng = event.lng;
          if (!drawingController.getCurrentDrawingState().isDrawing) {
            if (this.isGeoFileFeatureClick(lat, lng)) {
              return;
            }
            // Delegate to helper which validates and opens modal
            this.openWeatherModalIfInsideCountry(lat, lng);
          } else {
            if (!this.checkPointInsideCountry(lat, lng)) {
              drawingController.disableAllDrawingModes()
            }

          }
        });
    }

    // Subscribe to shape creation events
    if (this.openstreetmapComponent?.isMapInitialized) {
      const drawingController = this.openstreetmapComponent.getDrawingController();
      if (drawingController?.shapeCreated$) {
        drawingController.shapeCreated$
          .pipe(takeUntil(this.destroy$))
          .subscribe((shape) => {
            this.handleShapeCreated(shape);
          });
      }
    }
  }


  private handleMapRightClick(event: any): void {
    // Show context menu at the click position
    this.showContextMenu = true;
    this.contextMenuPosition = {
      top: event.containerPoint?.y || 0,
      left: event.containerPoint?.x || 0
    };

    // Store the click data for context menu actions
    this.contextMenuData = {
      lat: event.lat,
      lng: event.lng,
      originalEvent: event.originalEvent,
      containerPoint: event.containerPoint
    };

    // Close context menu when clicking elsewhere on the map
    setTimeout(() => {
      document.addEventListener('click', this.onDocumentClick.bind(this), {once: true});
    }, 100);
  }

  /**
   * Close context menu when clicking outside
   */
  private onDocumentClick(event: any): void {
    const contextMenuEl = document.querySelector('.map-context-menu');
    if (!contextMenuEl?.contains(event.target)) {
      this.showContextMenu = false;
    }
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    // Check if map is initialized before loading data
    if (!this.openstreetmapComponent?.isMapInitialized) {
      return;
    }
    // if (environment.client=='masofa'){
      this.loadRegions();
    // }
  }


  private syncRegionNamesVisibility(): void {
    this.mapController$.next({
      setShowRegionNames: this.mapOptions.showRegionNames,
    });
  }

  onToggleUgmStations(value: boolean): void {
    this.mapOptions.showUgmStations = value;

    if (this.mapOptions.showUgmStations) {
      try {
        this.weatherStationManager.initializeRegions(this.openstreetmapComponent);
        this.weatherStationManager.getUgmWeatherStations()
          .pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
          },
          error: (err: any) => {
            console.error('Failed to load UGM stations', err);
          }
        });
      } catch (e) {

      }
    } else {
      try {
        const markerIds = this.weatherStationManager.getWeatherStationMarkerIds?.() || [];
        markerIds.forEach((id: string) => {
          this.mapController$.next({removeMarker: {id}});
        });
        this.weatherStationManager.clearWeatherStationMarkers?.();
      } catch (e) {
        console.error('Error while disabling UGM stations', e);
      }

      if (this.selectedCrops) {
        this.switchDisplayMode('seasons');
        this.loadSeasonsByCropId(this.selectedCrops);
      } else {
        this.loadMonitoringPoints();
      }
    }
  }

  /**
   * Enable polygon drawing
   */

  openExportModal(event?: { latitude: number, longitude: number } | void): void {
    if (event) {
      this.exportCoordinates = event;
    } else {
      this.exportCoordinates = null;
    }
    this.showExportModal = true;
  }

  openMonitoringModal(): void {
    this.showMonitoringPointsModal = true;

    setTimeout(() => {
      const objects = this.monitoringPointManager.monitoringObjects$.value || [];
      const firstPoint = objects.find(obj => obj.pointJson?.lat != null && obj.pointJson?.lon != null);
      if (firstPoint?.pointJson) {
        this.gisMapControllerManager.flyTo(firstPoint.pointJson.lat!, firstPoint.pointJson.lon!, 12);
      } else {
        this.gisMapControllerManager.flyTo(this.mapCenter[0], this.mapCenter[1], 6);
      }
    }, 200);
  }

  /**
   * Handle shape created event from drawing controller
   */
  private handleShapeCreated(shape: MapDrawShapeCreatedPayload): void {
    // Measurement-only shapes: show area/perimeter on the map, do not validate or open save modal.
    if (shape.type === 'box-polygon' || shape.type === 'custom-polygon') {
      this.openstreetmapComponent.getDrawingController().setCurrentDrawingState({
        isDrawing: false,
        drawingType: null
      });
      this.mapOptions.drawingMode = false;
      this.openstreetmapComponent.getLayersController()?.enablePolygonInteractivity();
      return;
    }

    // Convert rectangle coordinates (2 points) to polygon coordinates (4 corners)
    let coordinatesForValidation: [number, number][];

    if (shape.type === 'rectangle' && shape.coordinates.length === 2) {
      // Rectangle has 2 opposite corners: [topLeft, bottomRight]
      const [corner1, corner2] = shape.coordinates;
      const lat1 = corner1[0];
      const lng1 = corner1[1];
      const lat2 = corner2[0];
      const lng2 = corner2[1];

      // Create 4 corners: topLeft, topRight, bottomRight, bottomLeft
      coordinatesForValidation = [
        [Math.max(lat1, lat2), Math.min(lng1, lng2)], // Top-left
        [Math.max(lat1, lat2), Math.max(lng1, lng2)], // Top-right
        [Math.min(lat1, lat2), Math.max(lng1, lng2)], // Bottom-right
        [Math.min(lat1, lat2), Math.min(lng1, lng2)]  // Bottom-left
      ];
      shape.coordinates = coordinatesForValidation;
    } else {
      coordinatesForValidation = shape.coordinates;
    }

    // Prepare country polygons from region polygons
    const regionCache = this.regionManagementManager.getRegionPolygonsCache();
    const countryPolygons = Array.from(regionCache.values()).map(entry => ({
      id: entry.polygon.id,
      coordinates: entry.polygon.coordinates
    }));

    // Prepare existing monitoring polygons from monitoring points
    const existingMonitoringPolygons: Array<{ coordinates: [number, number][] }> = [];

    // Get monitoring objects and extract their polygon coordinates
    this.monitoringPointManager.monitoringObjects$.value.forEach(obj => {
      if (obj.polygonJson && obj.polygonJson.length > 0) {
        const coordinates: [number, number][] = obj.polygonJson.map(coord => [
          coord.lat || 0,
          coord.lon || 0
        ]);
        existingMonitoringPolygons.push({coordinates});
      }
    });

    // Validate the drawn polygon
    const validation = this.polygonValidationService.validatePolygon(
      coordinatesForValidation,
      countryPolygons,
      existingMonitoringPolygons
    );

    if (!validation.isValid) {
      // Show error message to user
      let errorMessage: string;
      switch (validation.error) {
        case 'POLYGON_OUTSIDE_COUNTRY_BOUNDARIES':
          errorMessage = this.translate.instant('ERROR_POLYGON_OUTSIDE_BOUNDARIES');
          if (!errorMessage || errorMessage === 'ERROR_POLYGON_OUTSIDE_BOUNDARIES') {
            errorMessage = 'Polygon must be drawn within country boundaries';
          }
          break;
        case 'POLYGON_CROSSES_REGION_BOUNDARIES':
          errorMessage = this.translate.instant('ERROR_POLYGON_CROSSES_BOUNDARIES');
          if (!errorMessage || errorMessage === 'ERROR_POLYGON_CROSSES_BOUNDARIES') {
            errorMessage = 'Polygon cannot cross region boundaries';
          }
          break;
        case 'POLYGON_OVERLAPS_EXISTING':
          errorMessage = this.translate.instant('ERROR_POLYGON_OVERLAPS');
          break;
          case 'ERROR_POLYGON_SELF_INTERSECTING':
          errorMessage = this.translate.instant('ERROR_POLYGON_SELF_INTERSECTING');
          break;
        default:
          errorMessage = this.translate.instant('ERROR_INVALID_POLYGON');
          if (!errorMessage || errorMessage === 'ERROR_INVALID_POLYGON') {
            errorMessage = 'Invalid polygon';
          }
      }

      // Show error toast
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('ERROR'),
        detail: errorMessage,
        life: 5000
      });

      this.pendingUnsavedMonitoringDrawLayer = null;

      // Remove the invalid polygon from the map
      if (shape.layer) {
        this.openstreetmapComponent.getDrawingController()?.removeDrawnItemLayer(shape.layer);
      }

      // Reset drawing state
      this.openstreetmapComponent.getDrawingController().setCurrentDrawingState({
        isDrawing: false,
        drawingType: null
      })

      this.mapOptions.drawingMode = false;
      return;
    }

    // Polygon is valid, proceed with creating monitoring object
    this.pendingUnsavedMonitoringDrawLayer = shape.layer ?? null;

    // Calculate proper center (centroid) for polygon
    const center = PolygonConverterUtil.getPolygonCenter(shape.coordinates);

    // Convert coordinates to GeoJSON format for MapMonitoringObject
    const polygonJson = shape.coordinates.map(coord => ({
      lat: coord[0],
      lon: coord[1]
    }));

    // Create MapMonitoringObject-like data structure
    const monitoringObjectData = {
      name: shape.type === 'polygon' ? 'Drawn Polygon' : 'Drawn Rectangle',
      type: shape.type === 'polygon' ? 'Polygon' : 'Rectangle',
      polygonJson: polygonJson,
      pointJson: null,
      createAt: new Date().toISOString()
    };

    // Set the selected point for weather with polygon data
    if (center?.lng) {
      this.selectedPointForWeather = {
        latitude: center.lat,
        longitude: center.lng,
        data: monitoringObjectData
      };
    }

    this.mapOptions.drawingMode = false;

    // Show weather details modal automatically
    this.showWeatherDetailsModal = true;
    setTimeout(() => {
      try {
        this.reloadMonitoringPoints();
      } catch (e) {
        console.error('Error triggering reload of monitoring points after shape creation', e);
      }
    }, 300);
  }

  /**
   * Handle context menu add point - Opens weather details modal at clicked coordinates
   */
  addMonitoringPointAtContextMenu(): void {
    if (!this.contextMenuData) {
      return;
    }

    // Validate point before opening modal
    const lat = this.contextMenuData.lat;
    const lng = this.contextMenuData.lng;
    const opened = this.openWeatherModalIfInsideCountry(lat, lng);
    if (!opened) {
      // Close context menu even if point is invalid
      this.showContextMenu = false;
      return;
    }

    // Close context menu
    this.showContextMenu = false;
  }

  /**
   * Handle context menu zoom - Zooms map to the clicked location
   */
  onContextMenuZoom(): void {
    if (!this.contextMenuData) {
      return;
    }

    // Use mapController to zoom to the clicked coordinates and center on them
    this.mapController$.next({
      setCenter: {
        lat: this.contextMenuData.lat,
        lng: this.contextMenuData.lng,
        zoom: this.openstreetmapComponent.getMapInstance().getZoom() + 1,
      }
    });

    // Close context menu
    this.showContextMenu = false;
  }

  /**
   * Load monitoring points
   */
  private loadMonitoringPoints(): void {
    this.monitoringPointManager.initialize(this.openstreetmapComponent);

    // Show loading indicator
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }

    this.switchDisplayMode('monitoring')
    // Subscribe to marker clicks
    this.monitoringPointManager.markerClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.openWeatherModalAtCoordinates(event.lat, event.lng, event.data);
      });

    // Subscribe to polygon clicks
    this.monitoringPointManager.polygonClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.openWeatherModalAtCoordinates(event.lat, event.lng, event.data);
      });

    this.monitoringPointManager
      .loadMonitoringObjects(undefined)
      .subscribe({
        next: () => {
          // Hide loading indicator
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }
        },
        error: (err) => {
          // Hide loading indicator on error
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }
          console.error('Error loading monitoring objects:', err);
        }
      });
  }

  private openWeatherModalAtCoordinates(lat: number, lng: number, data: any): void {
    this.openWeatherModalIfInsideCountry(lat, lng, data);
  }

  checkPointInsideCountry(lat: number, lng: number): boolean {
    const regionCache = this.regionManagementManager.getRegionPolygonsCache();
    if (regionCache && regionCache.size > 0) {
      const point: [number, number] = [lat, lng];
      // Convert Map values to array of polygons
      const polygons = Array.from(regionCache.values()).map(entry => entry.polygon);
      const inside = this.polygonValidationService.isPointWithinCountry(point, polygons as any);
      if (!inside) {
        const errorMessage = this.translate.instant('ERROR_POINT_OUTSIDE_BOUNDARIES');
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: errorMessage,
          life: 5000
        });
        return false;
      }
    }
    return true
  }

  private openWeatherModalIfInsideCountry(lat: number, lng: number, data?: any): boolean {
    // If country polygons are available, validate the point before opening modal
    if (!this.checkPointInsideCountry(lat, lng)) {
      return false
    }

    const resolved = this.regionManagementManager.getRegionPathByPoint(lat, lng);
    this.selectedPointRegionPath = resolved.path;

    this.selectedPointForWeather = {
      latitude: lat,
      longitude: lng,
      data: data || {}
    };
    this.showWeatherDetailsModal = true;

    // Cache miss / no district yet (e.g. polygons still loading, or only oblast hit) → server.
    if (resolved.deepestLevel < 3) {
      this.regionByPointRequest$.next({lat, lng});
    }

    return true;
  }


  onOpenWeatherFromDialog(event: { lat: number, lng: number, data: any }): void {
    this.openWeatherModalAtCoordinates(event.lat, event.lng, event.data);
  }

  /**
   * Load seasons by crop ID
   */
  loadSeasonsByCropId(cropId: string): void {
    this.monitoringPointManager.initialize(this.openstreetmapComponent);

    // Show loading indicator
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }

    this.monitoringPointManager
      .loadSeasonsByCropId(cropId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Hide loading indicator
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }
        },
        error: () => {
          // Hide loading indicator on error
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }
        }
      });
  }

  /**
   * Switch between monitoring objects and seasons display
   */
  switchDisplayMode(mode: 'monitoring' | 'seasons'): void {
    this.monitoringPointManager.setDisplayMode(mode);
  }

  /**
   * Get current display mode
   */
  getRegionPathString(): string {
    return this.regionManagementManager.getRegionPathString(
      this.translate.instant(environment.country),
      this.translate.instant('ALL_REGIONS')
    );
  }

  getLastRegionPath(): string {
    return this.regionManagementManager.getLastRegionPath();
  }


  /**
   * Load regions
   */
  private loadRegions(): void {

    // Show loading indicator
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }

    this.regionManagementManager.toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({

        next: (polygons) => {
          // Hide loading indicator
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }

          if (polygons && polygons.length > 0) {

            // Configure polygon events using the manager
            this.regionManagementManager.configurePolygonEvents(
              polygons,
              this.mapOptions,
              (lat, lng, polygonData) => this.loadPolygonDataAndOpenModal(lat, lng, {})
            );

            // Add new filtered polygons
            this.mapController$.next({
              addGisPolygons: polygons
            });
            // Polygons are now cached in regionManagementManager.getRegionPolygonsCache()
            this.refreshLegendRange();

            this.mapOptions.showRegionNames = true;
            this.syncRegionNamesVisibility();

            this.weatherStationManager.initializeRegions(this.openstreetmapComponent);
          }
          this.loadMonitoringPoints();
        },
        error: (err) => {
          // Hide loading indicator on error
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }
          console.error('Error loading regions:', err);
        }
      });
  }

  private loadPolygonDataAndOpenModal(
    lat: number,
    lng: number,
    regionPolygonData: any
  ): void {
    if (this.isGeoFileFeatureClick(lat, lng)) {
      return;
    }

    const tryFindMatching = () => this.monitoringPointManager.monitoringObjects$.value.find((obj: any) => {
      if (!obj.polygonJson || obj.polygonJson.length === 0) {
        return false;
      }

      const a = obj.polygonJson.map((c: any) => ({lat: Number(c.lat) || 0, lon: Number(c.lon) || 0}));
      const b = (regionPolygonData.polygonJson || []).map((c: any) => ({
        lat: Number(c.lat) || 0,
        lon: Number(c.lon) || 0
      }));

      return polygonsAreEquivalent(a, b);
    });

    const tryFindMatchingByContainment = () => {
      const point = {lat, lon: lng};
      return this.monitoringPointManager.monitoringObjects$.value.find((obj: any) => {
        if (!obj.polygonJson || obj.polygonJson.length === 0) return false;
        const poly = obj.polygonJson.map((c: any) => ({lat: Number(c.lat) || 0, lon: Number(c.lon) || 0}));
        return pointInPolygon(point, poly);
      });
    };

    // First try containment (point inside monitoring polygon)
    let matchingMonitoringObject = tryFindMatchingByContainment();

    // Otherwise fall back to polygon equality
    if (!matchingMonitoringObject) {
      const tryFindMatching = () => this.monitoringPointManager.monitoringObjects$.value.find((obj: any) => {
        if (!obj.polygonJson || obj.polygonJson.length === 0) {
          return false;
        }

        const a = obj.polygonJson.map((c: any) => ({lat: Number(c.lat) || 0, lon: Number(c.lon) || 0}));
        const b = (regionPolygonData.polygonJson || []).map((c: any) => ({
          lat: Number(c.lat) || 0,
          lon: Number(c.lon) || 0
        }));
        return polygonsAreEquivalent(a, b);
      });

      matchingMonitoringObject = tryFindMatching();
    }

    if (matchingMonitoringObject) {
      this.selectedPointForWeather = {
        latitude: lat,
        longitude: lng,
        data: matchingMonitoringObject
      };
      this.openWeatherModalIfInsideCountry(lat, lng, matchingMonitoringObject);
      return;
    }

    this.selectedPointForWeather = {
      latitude: lat,
      longitude: lng,
    };
    this.openWeatherModalIfInsideCountry(lat, lng, regionPolygonData);

    setTimeout(() => {
      try {
        const foundLater = tryFindMatching();
        if (foundLater) {
          this.selectedPointForWeather = {
            latitude: lat,
            longitude: lng,
            data: foundLater
          };
          this.showWeatherDetailsModal = true;
        }
      } catch (e) {
      }
    }, 500);

    try {
      const currentList = this.monitoringPointManager.monitoringObjects$.value;
      if (!Array.isArray(currentList) || currentList.length === 0) {
        // Subscribe and take first non-empty emission
        this.monitoringPointManager.monitoringObjects$
          .pipe(
            filter((arr: any[]) => Array.isArray(arr) && arr.length > 0),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            const found = tryFindMatching();
            if (found) {
              this.selectedPointForWeather = {
                latitude: lat,
                longitude: lng,
                data: found
              };
              this.showWeatherDetailsModal = true;
            }
          });
      }
    } catch (e) {
    }
  }


  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.applyDefaultWeatherLayerOnLoad = false;
    this.filterModel = {
      DATA_SOURCE: "era5",
      DATE: new Date(),
      YEAR: new Date().getFullYear(),
      DATA_TYPE: "weather",
      WEATHER_LAYER: null,
    };
    this.selectedWeatherLayer = null;
    this.selectedDataType = null;
    this.selectedDataSource = null;
    this.selectedDate = new Date()
    this.selectedCrops = null;
    this.processFilterTypeAndSource();
    this.loadMonitoringPoints();

    // Reset map to default state
    this.resetMapFilters();
  }

  /**
   * Apply filters to map data
   */
  /**
   * Reset map filters to default state
   */
  private resetMapFilters(): void {
    const layersController = this.openstreetmapComponent?.getLayersController();

    // Remove weather layers
    this.removeWeatherTileLayer();
    this.showWeatherLegend = false;

    if (this.monitoringPointManager) {
      this.monitoringPointManager.setDisplayMode('monitoring');
    }

    if (this.regionManagementManager) {
      if (layersController) {
        this.regionManagementManager.removeHighlight(layersController)
      }
    }
    // Reset map view to default

  }

  /**
   * Handle weather layer change from filter
   */
  private onWeatherLayerChange(): void {
    this.refreshLegendRange();
    // Remove existing weather tile layer if present
    this.removeWeatherTileLayer();

    // Check if both WEATHER_LAYER and DATE are set
    if (this.filterModel.WEATHER_LAYER && (this.filterModel.DATE || this.filterModel.YEAR)) {
      this.selectedWeatherLayer = this.filterModel.WEATHER_LAYER;
      this.showWeatherLegend = this.layerStates.weather.visible;
      this.topLayer = 'weather';
      this.addWeatherTileLayer();
    } else {
      this.showWeatherLegend = false;
    }
  }

  private refreshLegendRange(): void {
    this.legendRangeCancel$.next();
    const dataType = this.filterModel?.DATA_TYPE;
    const layer = this.filterModel?.WEATHER_LAYER;
    if (!dataType || !layer) {
      this.legendClipToRange = false;
      this.legendDisplayedRange = null;
      return;
    }

    this.legendClipToRange = true;
    this.legendDisplayedRange = null;
    this.legendRangeService.load(dataType, layer, this.countryPointTest())
      .pipe(takeUntil(this.legendRangeCancel$), takeUntil(this.destroy$))
      .subscribe((range) => {
        if (!range) {
          this.legendClipToRange = false;
          this.legendDisplayedRange = null;
          return;
        }
        this.legendDisplayedRange = range;
      });
  }

  private countryPointTest(): ((latitude: number, longitude: number) => boolean) | undefined {
    const polygons = Array.from(this.regionManagementManager.getRegionPolygonsCache().values())
      .map((entry) => entry.polygon);
    if (!polygons.length) {
      return undefined;
    }
    return (latitude, longitude) =>
      this.polygonValidationService.isPointWithinCountry([latitude, longitude], polygons);
  }

  /**
   * Add weather tile layer to the map
   */
  private addWeatherTileLayer(): void {
    if (!this.openstreetmapComponent?.isMapInitialized) {
      return;
    }

    const mapInstance = this.openstreetmapComponent.getMapInstance();
    if (!mapInstance) {
      return;
    }

    // Show loading indicator
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }

    // Determine base URL based on data source
    let baseUrl = this.mapLayers['weather'].baseUrl;


    const selectedLayer = this.filterModel.WEATHER_LAYER;
    // Format date as YYYY-M-D (without leading zeros)

    // Create custom tile layer that converts x,y,z to lat,lon
    const WeatherTileLayer = (window as any).L.TileLayer.extend({
      getTileUrl: function (coords: any) {
        const zoom = coords.z;
        const x = coords.x;
        const y = coords.y;
        const n = Math.PI - 2 * Math.PI * (y + 0.5) / Math.pow(2, zoom);
        const lat = (180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))));
        const lon = (x + 0.5) / Math.pow(2, zoom) * 360 - 180;
        return `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=${selectedLayer}&coordinateSystem=0`;
      },
    });

    // Create and add the weather tile layer with opacity
    this.weatherTileLayer = new WeatherTileLayer('', {
      attribution: '© Weather Data',
      opacity: (this.layerStates.weather.visible ? this.layerStates.weather.opacity : 0) / 100,
      maxZoom: 30,
      minZoom: 1,
      className: 'weather-tile-layer'
    });

    // Listen for tile loading events
    this.weatherTileLayer.on('loading', () => {
      if (this.openstreetmapComponent) {
        this.openstreetmapComponent.isLoading = true;
      }
    });

    this.weatherTileLayer.on('load', () => {
      // Hide loading indicator when tiles are loaded
      if (this.openstreetmapComponent) {
        this.openstreetmapComponent.isLoading = false;
      }
    });

    this.weatherTileLayer.on('tileerror', () => {
      // Hide loading indicator on error
      if (this.openstreetmapComponent) {
        this.openstreetmapComponent.isLoading = false;
      }
    });

    this.weatherTileLayer.addTo(mapInstance);
    this.syncOverlayLayerOrder();

    // Also hide loading after a short delay as a fallback
    setTimeout(() => {
      if (this.openstreetmapComponent) {
        this.openstreetmapComponent.isLoading = false;
      }
    }, 2000);
  }

  /**
   * Remove weather tile layer from the map
   */
  private removeWeatherTileLayer(): void {
    if (this.weatherTileLayer && this.openstreetmapComponent?.isMapInitialized) {
      const mapInstance = this.openstreetmapComponent.getMapInstance();
      if (mapInstance) {
        mapInstance.removeLayer(this.weatherTileLayer);
        this.weatherTileLayer = null;
      }
    }
  }

  toggleLayerVisibility(layerKey: OverlayLayerKey): void {
    const nextVisible = !this.layerStates[layerKey].visible;
    this.layerStates[layerKey] = {
      ...this.layerStates[layerKey],
      visible: nextVisible
    };

    if (layerKey === 'weather') {
      this.applyWeatherLayerVisibility();
    } else if (layerKey === 'index') {
      this.openstreetmapComponent?.osmIndexOverlay?.setIndexLayerVisibility(nextVisible);
    } else {
      if (this.openstreetmapComponent) {
        this.openstreetmapComponent.setSatelliteLayerOpacityValue(
          nextVisible ? this.layerStates.satellite.opacity : 0,
        );
      }
    }

    if (nextVisible && layerKey !== 'satellite') {
      this.topLayer = layerKey;
      this.syncOverlayLayerOrder();
    }
  }

  onLayerOpacityChange(layerKey: OverlayLayerKey, opacity: number): void {
    const nextOpacity = Math.max(0, Math.min(100, opacity));
    this.layerStates[layerKey] = {
      ...this.layerStates[layerKey],
      opacity: nextOpacity
    };

    if (layerKey === 'weather') {
      this.applyWeatherLayerOpacity();
      if (this.layerStates.weather.visible) {
        this.topLayer = 'weather';
      }
    } else if (layerKey === 'index') {
      this.openstreetmapComponent?.osmIndexOverlay?.setIndexLayerOpacity(nextOpacity);
      if (this.layerStates.index.visible) {
        this.topLayer = 'index';
      }
    } else if (this.openstreetmapComponent) {
      this.openstreetmapComponent.setSatelliteLayerOpacityValue(
        this.layerStates.satellite.visible ? nextOpacity : 0,
      );
    }

    this.syncOverlayLayerOrder();
  }

  private applyWeatherLayerVisibility(): void {
    this.showWeatherLegend = !!this.selectedWeatherLayer && this.layerStates.weather.visible;
    this.applyWeatherLayerOpacity();
  }

  private applyWeatherLayerOpacity(): void {
    if (!this.weatherTileLayer?.setOpacity) {
      return;
    }

    const opacity = this.layerStates.weather.visible ? this.layerStates.weather.opacity / 100 : 0;
    this.weatherTileLayer.setOpacity(opacity);
  }

  private syncOverlayLayerOrder(): void {
    const hasVisibleWeather = !!this.selectedWeatherLayer && this.layerStates.weather.visible && this.weatherTileLayer?.bringToFront;
    const hasVisibleIndex = !!this.openstreetmapComponent?.osmIndexOverlay?.hasActiveIndexLayer() && this.layerStates.index.visible;

    if (hasVisibleWeather && hasVisibleIndex) {
      this.openstreetmapComponent?.osmIndexOverlay?.bringIndexLayerToFront();
      this.weatherTileLayer.bringToFront();
      return;
    }

    if (hasVisibleIndex) {
      this.openstreetmapComponent?.osmIndexOverlay?.bringIndexLayerToFront();
    }

    if (hasVisibleWeather) {
      this.weatherTileLayer.bringToFront();
    }
  }

  filterFieldsChange() {
    // Handle data source changes

    const layersController = this.openstreetmapComponent?.getLayersController();
    this.regionManagementManager.filterByRegion(this.filterModel?.regionId, layersController);
    this.monitoringPointManager.loadMonitoringObjects(this.filterModel?.regionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    // Check if DATA_TYPE, DATA_SOURCE, DATE, or WEATHER_LAYER have changed
    const dataTypeChanged = this.previousDataType !== undefined && this.previousDataType !== this.filterModel.DATA_TYPE;
    const dataSourceChanged = this.previousDataSource !== undefined && this.previousDataSource !== this.filterModel.DATA_SOURCE;
    const dateChanged = this.previousDate !== undefined && this.previousDate !== this.filterModel.DATE;
    let yearChanged = this.previousYear !== undefined && this.previousYear !== this.getSelectedYear();
    const weatherLayerChanged = this.selectedWeatherLayer !== this.filterModel.WEATHER_LAYER;

    if (weatherLayerChanged && !this.filterModel.WEATHER_LAYER) {
      this.applyDefaultWeatherLayerOnLoad = false;
      this.selectedWeatherLayer = null;
      this.removeWeatherTileLayer();
      this.showWeatherLegend = false;
    }

    if (yearChanged) {
      this.snapDateToYear(this.getSelectedYear());
    } else if (dateChanged) {
      const dateYear = this.getFilterDateYear();
      if (dateYear && dateYear !== this.getSelectedYear()) {
        this.filterModel.YEAR = dateYear;
        yearChanged = true;
      }
    }

    const reloadDates = dataTypeChanged || dataSourceChanged || yearChanged;
    const reloadLayers = dataTypeChanged || dataSourceChanged || dateChanged || yearChanged || weatherLayerChanged;

    if (reloadDates) {
      this.tilesManager.GetWeatherLayerDates(
        this.getSelectedYear(),
        this.filterModel.DATA_TYPE,
        this.filterModel.DATA_SOURCE
      )
        .pipe(
          switchMap(() => {
            const dateAdjusted = yearChanged && this.constrainDateToAvailableDates();
            return (reloadLayers || dateAdjusted) ? this.processFilterTypeAndSource$() : of(null);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    } else if (reloadLayers) {
      this.processFilterTypeAndSource();
    }

    if (this.filterModel.CROPS) {
      const cropId = this.filterModel.CROPS;
      this.selectedCrops = cropId;
      this.loadSeasonsByCropId(cropId);

      if (this.selectedCrops){
        const crops = this.monitoringPointManager.crops$.value
        this.weatherLayerOptions = CropsManagerService.filterBaseTempByCrop(this.weatherLayerOptions, this.selectedCrops, crops );
      }

      this.switchDisplayMode('seasons');

    } else {
      this.selectedCrops = null;
      const cropChanged = this.previousCrop !== undefined && this.previousCrop !== this.filterModel.CROPS;

      // If crop changed and is now null or undefined, load monitoring points
      if (cropChanged) {
        this.processFilterTypeAndSource();
        this.switchDisplayMode('monitoring');
        // this.loadMonitoringPoints();
      }
    }
    this.previousCrop = this.filterModel.CROPS;

  }


  /**
   * Extract layer name without date/year suffix.
   * Example: "EraWeatherHumidity20251211" -> "Humidity"
   */
  private getLayerNameWithoutDate(layerName: string): string {
    return stripWeatherLayerPeriod(layerName);
  }

  private static readonly DEFAULT_WEATHER_LAYER_EN_NAME = 'Temperature Average';

  private findTemperatureAverageLayer(layers: any[]): any | undefined {
    return layers?.find((l: any) => l?.names?.['en-US'] === GisPageRefactoredNewComponent.DEFAULT_WEATHER_LAYER_EN_NAME);
  }

  private applyWeatherLayerAfterOptionsLoaded(periodChanged: boolean): void {
    if (periodChanged && this.filterModel.WEATHER_LAYER) {
      const previousLayerName = this.filterModel.WEATHER_LAYER;
      const oldLayerNameWithoutDate = this.getLayerNameWithoutDate(previousLayerName);
      const preferYearly = isYearlyWeatherLayerName(previousLayerName);
      const oldLayerExists = this.weatherLayerOptions.find((l: any) => {
        const name = l?.geoServerName;
        if (!name || stripWeatherLayerPeriod(name) !== oldLayerNameWithoutDate) {
          return false;
        }
        return preferYearly === isYearlyWeatherLayerName(name);
      });
      if (oldLayerExists) {
        this.selectedWeatherLayer = oldLayerExists.geoServerName;
        this.filterModel.WEATHER_LAYER = oldLayerExists.geoServerName;
        this.onWeatherLayerChange();
        return;
      }
      this.filterModel.WEATHER_LAYER = null;
    }

    if (this.filterModel.WEATHER_LAYER) {
      this.applyDefaultWeatherLayerOnLoad = false;
      this.selectedWeatherLayer = this.filterModel.WEATHER_LAYER;
      this.onWeatherLayerChange();
      return;
    }

    if (this.applyDefaultWeatherLayerOnLoad) {
      const defaultLayer = this.findTemperatureAverageLayer(this.weatherLayerOptions);
      if (defaultLayer?.geoServerName) {
        this.filterModel.WEATHER_LAYER = defaultLayer.geoServerName;
        this.selectedWeatherLayer = defaultLayer.geoServerName;
        this.applyDefaultWeatherLayerOnLoad = false;
        this.onWeatherLayerChange();
        return;
      }
    }

    this.removeWeatherTileLayer();
    this.showWeatherLegend = false;
  }

  processFilterTypeAndSource() {
    this.processFilterTypeAndSource$()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private processFilterTypeAndSource$(): Observable<unknown> {
    if (!this.filterModel.DATA_TYPE) {
      return of(null);
    }

    const dt = String(this.filterModel.DATA_TYPE || '');
    let normalized = dt.length ? dt.charAt(0).toUpperCase() + dt.slice(1) : dt;

    const filterDate = this.toFilterDate(this.filterModel.DATE) ?? new Date();
    let selectedDate: string | null;
    if (normalized.includes('Normal')) {
      selectedDate = DataTransformer.formatDateToMMdd(filterDate);
    } else {
      selectedDate = DataTransformer.formatDateToYYYYMMDD(filterDate);
    }

    if (normalized.includes('Weather')) {
      normalized = ''
    }

    let normalizedDs = '';

    if (this.filterModel.DATA_SOURCE === 'ugm') {
      normalizedDs = 'Ugm'
    } else if (this.filterModel.DATA_SOURCE === 'ibm') {
      normalizedDs = 'Ibm'
    }

    const filters = [
      {
        filterField: 'geoServerName',
        filterValue: selectedDate as string,
        filterOperator: normalized.includes('Normal')
          ? FilterOperator.EndsWith
          : FilterOperator.Contains
      },
    ]

    if (normalized !== '') {
      filters.push(
        {
          filterField: 'geoServerName',
          filterValue: normalized,
          filterOperator: FilterOperator.Contains
        },
      )
    }
    if (normalizedDs !== '') {
      filters.push({
        filterField: 'geoServerName',
        filterValue: normalizedDs,
        filterOperator: FilterOperator.Contains
      },)
    }

    const yearlyFilters = this.buildYearlyLayerFilters(normalized, normalizedDs);

    const dataSourceChanged = this.previousDataSource !== null &&
      this.previousDataSource !== this.filterModel.DATA_SOURCE;
    const dataTypeChanged = this.previousDataType !== null &&
      this.previousDataType !== this.filterModel.DATA_TYPE;
    const dateChanged = this.previousDate !== null &&
      this.previousDate !== this.filterModel.DATE;
    const yearChanged = this.previousYear !== null &&
      this.previousYear !== this.getSelectedYear();
    const periodChanged = dateChanged || yearChanged;

    if (dataSourceChanged || dataTypeChanged) {
      // Reset WEATHER_LAYER when DATA_SOURCE or DATA_TYPE changes
      this.filterModel.WEATHER_LAYER = null;
      this.removeWeatherTileLayer();
      this.showWeatherLegend = false;
    }

    if (periodChanged) {
      this.removeWeatherTileLayer();
    }

    this.previousDataSource = this.filterModel.DATA_SOURCE;
    this.previousDataType = this.filterModel.DATA_TYPE;
    this.previousDate = this.filterModel.DATE;
    this.previousYear = this.getSelectedYear();

    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }

    const daily$ = this.tilesManager.loadTileLayers({filters});
    const yearly$ = yearlyFilters
      ? this.tilesManager.loadTileLayers({filters: yearlyFilters}).pipe(catchError(() => of([])))
      : of([]);

    return forkJoin({daily: daily$.pipe(catchError(() => of([]))), yearly: yearly$}).pipe(
      tap(({daily, yearly}) => {
        if (this.openstreetmapComponent) {
          this.openstreetmapComponent.isLoading = false;
        }
        this.weatherLayerOptions = this.finalizeWeatherLayerOptions(
          this.filterNormalLayersForSelectedDate(this.mergeTileLayers(daily, yearly))
        );

        if (this.filterModel.DATA_SOURCE === 'era5') {
          this.weatherLayerOptions = this.weatherLayerOptions.filter((l: any) =>
            !l.geoServerName.includes('Ugm') &&
            !l.geoServerName.includes('Ibm') &&
            !l.geoServerName.includes('ARVI') &&
            !l.geoServerName.includes('EVI') &&
            !l.geoServerName.includes('GNDVI') &&
            !l.geoServerName.includes('MNDWI') &&
            !l.geoServerName.includes('NDMI') &&
            !l.geoServerName.includes('NDVI') &&
            !l.geoServerName.includes('ORVI') &&
            !l.geoServerName.includes('OSAVI')
          );
        }

        if (this.filterModel.DATA_TYPE === 'weather') {
          this.weatherLayerOptions = this.weatherLayerOptions.filter(
            (l: any) => !isExcludedFromWeatherDataType(l.geoServerName)
          );
        }

        this.applyYearlyAtdDropdownNames(this.weatherLayerOptions);

        if (this.selectedCrops) {
          const crops = this.monitoringPointManager.crops$.value;
          this.weatherLayerOptions = CropsManagerService.filterBaseTempByCrop(
            this.weatherLayerOptions,
            this.selectedCrops,
            crops
          );
        }

        this.applyWeatherLayerAfterOptionsLoaded(periodChanged);
      })
    );
  }

  /** Norm tiles are keyed by MMdd; drop other days if the API returns a broad set. */
  private filterNormalLayersForSelectedDate(layers: any[]): any[] {
    if (this.filterModel.DATA_TYPE !== 'normal') {
      return layers;
    }
    const filterDate = this.toFilterDate(this.filterModel.DATE) ?? new Date();
    const mmdd = DataTransformer.formatDateToMMdd(filterDate);
    if (!mmdd) {
      return layers;
    }
    return layers.filter((layer) => {
      const name = layer?.geoServerName as string | undefined;
      if (!name || !/Normal/i.test(name)) {
        return true;
      }
      return name.endsWith(mmdd);
    });
  }

  private buildYearlyLayerFilters(
    typeToken: string,
    sourceToken: string
  ): Array<{filterField: string; filterValue: string; filterOperator: FilterOperator}> | null {
    if (this.filterModel.DATA_TYPE === 'normal') {
      return null;
    }
    const year = this.getSelectedYear();
    if (!year) {
      return null;
    }
    const filters = [
      {
        filterField: 'geoServerName',
        filterValue: String(year),
        filterOperator: FilterOperator.EndsWith
      }
    ];
    if (typeToken) {
      filters.push({
        filterField: 'geoServerName',
        filterValue: typeToken,
        filterOperator: FilterOperator.Contains
      });
    }
    if (sourceToken) {
      filters.push({
        filterField: 'geoServerName',
        filterValue: sourceToken,
        filterOperator: FilterOperator.Contains
      });
    }
    return filters;
  }

  private weatherLayerSelectOptions(): any[] {
    return mapWeatherLayerSelectOptions(
      this.weatherLayerOptions,
      this.translate,
      this.serverTranslate
    );
  }

  private finalizeWeatherLayerOptions(layers: any[]): any[] {
    return mapWeatherLayerSelectOptions(layers, this.translate, this.serverTranslate);
  }

  private mergeTileLayers(daily: any, yearly: any): any[] {
    const byName = new Map<string, any>();
    for (const layer of [...this.asLayerArray(daily), ...this.asLayerArray(yearly)]) {
      const name = layer?.geoServerName;
      if (!name || byName.has(name)) {
        continue;
      }
      byName.set(name, layer);
    }
    return [...byName.values()];
  }

  private asLayerArray(layers: any): any[] {
    return Array.isArray(layers) ? layers : [];
  }

  private applyYearlyAtdDropdownNames(layers: any[]): void {
    const dictionaries = this.translate.translations as Record<string, Record<string, string>>;
    for (const layer of layers) {
      const titleKey = yearlyAtdDropdownTitleKey(layer?.geoServerName);
      if (!titleKey) {
        continue;
      }
      const names = this.asNamesRecord(layer.names);
      for (const [lang, dict] of Object.entries(dictionaries || {})) {
        const raw = dict?.[titleKey];
        if (typeof raw === 'string' && raw) {
          names[lang] = yearlyAtdDropdownLabel(raw);
        }
      }
      const currentLang = this.translate.currentLang;
      if (currentLang && !names[currentLang]) {
        names[currentLang] = yearlyAtdDropdownLabel(this.translate.instant(titleKey));
      }
      layer.names = names;
    }
  }

  private asNamesRecord(names: unknown): Record<string, string> {
    if (names && typeof names === 'object' && !Array.isArray(names)) {
      return {...(names as Record<string, string>)};
    }
    if (typeof names === 'string' && names) {
      try {
        const parsed = JSON.parse(names);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return {...parsed};
        }
      } catch {
        return {};
      }
    }
    return {};
  }

  private getSelectedYear(): number {
    const year = this.filterModel?.YEAR;
    if (typeof year === 'number' && year > 0) {
      return year;
    }
    if (year instanceof Date && !isNaN(year.getTime())) {
      return year.getFullYear();
    }
    return this.getFilterDateYear() ?? new Date().getFullYear();
  }

  private getFilterDateYear(): number | null {
    const date = this.toFilterDate(this.filterModel?.DATE);
    return date ? date.getFullYear() : null;
  }

  private toFilterDate(value: unknown): Date | null {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    if (typeof value === 'string' && value) {
      const isoDay = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDay) {
        return new Date(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3]));
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value === 'number' && value > 0) {
      const parsed = new Date(value, 0, 1);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  private snapDateToYear(year: number): void {
    const current = this.toFilterDate(this.filterModel.DATE) ?? new Date();
    if (current.getFullYear() === year) {
      return;
    }
    const next = new Date(current.getFullYear(), current.getMonth(), current.getDate());
    next.setFullYear(year);
    this.filterModel.DATE = next;
  }

  private constrainDateToAvailableDates(): boolean {
    const available = this.tilesManager.getAllAvailableDatesByIndex(
      this.filterModel.DATA_TYPE || 'weather',
      this.filterModel.DATA_SOURCE || 'era5'
    );
    const parsed = (available || [])
      .map((entry) => this.toFilterDate(entry))
      .filter((date): date is Date => !!date);
    if (!parsed.length) {
      return false;
    }
    const current = this.toFilterDate(this.filterModel.DATE);
    const currentKey = current ? this.toDateKey(current) : null;
    if (currentKey && parsed.some((date) => this.toDateKey(date) === currentKey)) {
      return false;
    }
    parsed.sort((a, b) => a.getTime() - b.getTime());
    this.filterModel.DATE = parsed[parsed.length - 1];
    return true;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  reloadMonitoringPoints(newMapPoint?: {
    latitude: number,
    longitude: number,
    name?: string ,
    result?: MapMonitoringObject
  }) {
    // Show loading indicator

    if (newMapPoint && newMapPoint.result) {
      this.monitoringPointManager.updateMonitoringPolygon(newMapPoint.result);
    }

    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }
    // Clear current layers and reload from server, subscribing to trigger the request
    this.monitoringPointManager.clearMonitoringLayers();
    const preserveSeasonsView = !!this.selectedCrops;
    this.monitoringPointManager.loadMonitoringObjects(undefined, {preserveDisplayMode: preserveSeasonsView})
      .subscribe({
      next: () => {
        if (preserveSeasonsView) {
          this.switchDisplayMode('seasons');
        }
        if (this.openstreetmapComponent) {
          this.openstreetmapComponent.isLoading = false;
        }
      },
      error: (err) => {
        if (this.openstreetmapComponent) {
          this.openstreetmapComponent.isLoading = false;
        }
        console.error('Error reloading monitoring points:', err);
      }
    });
  }

  afterCloseWeatherDetailsModal() {
    this.showWeatherDetailsModal = false;
    this.mapOptions.drawingMode = false;

    const drawingController = this.openstreetmapComponent?.getDrawingController();

    if (!this.selectedPointForWeather?.data?.id && this.pendingUnsavedMonitoringDrawLayer) {
      drawingController?.removeDrawnItemLayer(this.pendingUnsavedMonitoringDrawLayer);
    }
    this.pendingUnsavedMonitoringDrawLayer = null;

    drawingController?.ensureDrawingToolReleased();
    this.openstreetmapComponent?.syncDrawingToolbarState();
  }
}
